import {
  withResponsiveOptions,
  withBreakpointObserver,
  getClosestParent,
} from '@studiometa/js-toolkit';
import { MenuBtn, MenuList, Menu as MenuCore } from '@studiometa/ui';

/**
 * @param {*} childInstance
 * @param {*} parentInstance
 * @returns {boolean}
 */
function fixedIsDirectChild(childInstance, parentInstance) {
  return getClosestParent(childInstance, parentInstance.constructor) === parentInstance;
}

/**
 * Menu class.
 */
export default class Menu extends withResponsiveOptions(withBreakpointObserver(MenuCore)) {
  static config = {
    ...MenuCore.config,
    emits: ['open', 'close'],
    options: {
      ...MenuCore.config.options,
      mode: {
        type: String,
        default: 'click',
        responsive: true,
      },
    },
    components: {
      MenuList,
      MenuBtn,
    },
  };

  /**
   * Délai (ms) avant fermeture au survol, pour laisser le temps de passer du
   * bouton au panneau (qui s'ouvre sous le header, avec un espace entre les deux).
   */
  closeDelay = 250;

  /**
   * Timeout de fermeture en attente.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  closeTimeout = null;

  /**
   * isOpen
   * @returns {boolean}
   */
  get isOpen() {
    return this.menuList?.isOpen ?? false;
  }

  /**
   * Programme une fermeture différée ; annulée si le curseur revient sur le
   * bouton ou le panneau avant l'échéance (vérif via isHover).
   */
  scheduleClose() {
    if (this.shouldReactOnClick) {
      return;
    }

    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => {
      if (!this.isHover) {
        this.close();
      }
    }, this.closeDelay);
  }

  /**
   * Annule une fermeture différée en attente.
   */
  cancelClose() {
    clearTimeout(this.closeTimeout);
  }

  /**
   * Ouvre le menu au survol du bouton, en annulant toute fermeture en attente.
   * @param {object} props
   * @param {HTMLElement} props.target
   */
  onMenuBtnMouseenter(props) {
    this.cancelClose();
    super.onMenuBtnMouseenter(props);
  }

  /**
   * Fermeture différée au lieu de la fermeture immédiate (nextTick) du parent.
   */
  onMenuBtnMouseleave() {
    this.scheduleClose();
  }

  /**
   * Fermeture différée à la sortie du panneau.
   */
  onMenuListMouseleave() {
    this.scheduleClose();
  }

  /**
   * Nettoyage.
   */
  destroyed() {
    this.cancelClose();
  }

  /**
   * onMenuListItemsOpen
   * @param {*} props
   */
  onMenuListItemsOpen(props) {
    if (fixedIsDirectChild(props.target, this)) {
      super.onMenuListItemsOpen(props);
      this.$emit('open');
    }
  }

  /**
   * @param {object} props
   * @param {HTMLElement} props.target
   */
  onMenuListItemsClose({ target }) {
    if (fixedIsDirectChild(target, this)) {
      this.$emit('close');
    }
  }

  /**
   * @param {object} props
   * @param {Event} props.event
   * @param {HTMLElement} props.target
   */
  onMenuBtnClick({ target, event }) {
    if (fixedIsDirectChild(target, this) && this.shouldReactOnClick) {
      event.preventDefault();
      this.toggle();
    }
  }
}
